import { Component, DestroyRef, effect, inject, input, output } from '@angular/core';
import { ToggleButtonComponent } from "@controls/toggle-button/toggle-button.component";
import { ExportTypeEnum } from '@models/common/export-type.enum';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from "@ng-bootstrap/ng-bootstrap";
import {
  IShareholdersPatchModel,
  IShareholdersViewModel
} from "@pages/companies/models/company-shareholders-state.interface";
import {
  IndividualEntityDividerComponent
} from "@pages/individuals/components/individual-profile-page/components/individual-entity-divider/individual-entity-divider.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import {
  SecurityholderProfileComponent
} from "@shared/components/entity-profiles/entity-securityholders-view/components/secutityholder-profile/securityholder-profile.component";
import {
  EntitySecurityholdersGridService
} from "@shared/components/entity-profiles/entity-securityholders-view/entity-securityholders-grid.service";
import {
  SecurityholderViewModel
} from "@shared/components/entity-profiles/entity-securityholders-view/securityholder-view-model";
import { AgOpenActionComponent } from "@shared/components/grid/ag-cells/common/ag-open-action/ag-open-action.component";
import {
  GridClientSideComponent
} from "@shared/components/grid/components/grid-client-side/grid-client-side.component";
import { excelReportTitleStyles } from "@shared/utils/excel-title-for-report";
import { IsFullWidthRowParams } from "ag-grid-community";
import { ExcelStyle, GridApi, GridOptions, RowHeightParams } from "ag-grid-enterprise";

@Component({
  selector: 'app-entity-securityholders-view',
  standalone: true,
  imports: [
    SecurityholderProfileComponent,
    ToggleButtonComponent,
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    LoaderComponent,
    GridClientSideComponent
  ],
  providers: [EntitySecurityholdersGridService],
  templateUrl: 'entity-securityholders-view.component.html',
  styleUrl: 'entity-securityholders-view.component.scss',
})
export class EntitySecurityholdersViewComponent {
  entitySecurityholdersGridService = inject(EntitySecurityholdersGridService);
  destroyRef = inject(DestroyRef);

  isLoading = input.required<boolean>();
  showCompanyActions = input<boolean>(false);
  shareholdersViewModel = input.required<IShareholdersViewModel>();

  refreshShareholders = output<void>();
  patchShareholdersViewModel = output<IShareholdersPatchModel>();
  selectShareholders = output<SecurityholderViewModel[]>();
  changeShareholderPartyName = output<string[]>();
  changeShareholderAddress = output<string[]>();
  exportSecurityholders = output<ExportTypeEnum>();

  constructor() {
    effect(() => this.setColDefs(this.shareholdersViewModel().isPendingChangesVisible));
  }

  onRefreshShareholders(): void {
    this.refreshShareholders.emit();
  }

  setIsPastShareholdersVisible(isPastShareholdersVisible: boolean): void {
    const shareholderDetails = this.shareholdersViewModel().shareholderDetails;

    this.patchShareholdersViewModel.emit({ shareholderDetails, isPastShareholdersVisible });
  }

  setShareholdersDetails(shareholder: SecurityholderViewModel | null): void {
    const isPastShareholdersVisible = this.shareholdersViewModel().isPastShareholdersVisible;
    const shareholderDetails = shareholder?.record ?? null;

    this.patchShareholdersViewModel.emit({ shareholderDetails, isPastShareholdersVisible });
  }

  // =====  Grid  =====

  readonly gridOptions: GridOptions = {
    fullWidthCellRenderer: IndividualEntityDividerComponent,
    isFullWidthRow: (params: IsFullWidthRowParams<unknown>): boolean => typeof params.rowNode.data == 'string',
    getRowHeight: (params: RowHeightParams): number | undefined => typeof params.data == 'string' ? 24 : undefined,
  };

  colDefs = this.entitySecurityholdersGridService.colDefs;

  onGridReady(gridApi: GridApi): void {
    this.entitySecurityholdersGridService.setGridApi(gridApi);
  }

  onSelectShareholders(models: (SecurityholderViewModel | string)[]): void {
    const filteredModels = models.filter((model) => typeof model != 'string');
    this.selectShareholders.emit(filteredModels as SecurityholderViewModel[]);
  }

  setColDefs(isPendingChangesVisible = this.shareholdersViewModel().isPendingChangesVisible): void {
    this.entitySecurityholdersGridService.setColDefsWithOpenShareholderActions(
      {
        field: '',
        width: 50,
        suppressMovable: true,
        resizable: false,
        sortable: false,

        cellClass: 'actions-button-cell',
        cellRenderer: AgOpenActionComponent,
        cellRendererParams: {
          open: this.setShareholdersDetails.bind(this)
        },
      },
      isPendingChangesVisible,
      this.setShareholdersDetails.bind(this)
    );
  }

  // =====  Export  =====

  readonly ExportTypeEnum = ExportTypeEnum;
  readonly excelStyles: ExcelStyle[] = excelReportTitleStyles;

  onExportSecurityholders(exportType: ExportTypeEnum): void {
    this.exportSecurityholders.emit(exportType);
  }
}
